/**
 * Reusable KPI card and Aging summary widget used by AR/AP/Banks/Inventory
 * overview tabs. Kept small and composable — no external state.
 */
import { formatCurrency } from "@/lib/format";

export function StatCard({ label, value, sublabel, tone = "default", currency, testid }) {
  const toneCls =
    tone === "brand" ? "border-l-4 border-l-[var(--brand)]" :
    tone === "warn" ? "border-l-4 border-l-[#D4A017]" :
    tone === "danger" ? "border-l-4 border-l-[var(--expense)]" :
    "";
  const numDisplay = currency
    ? formatCurrency(value, currency)
    : (typeof value === "number" ? value.toLocaleString() : value);
  return (
    <div data-testid={testid} className={`card-flat p-4 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">{label}</div>
      <div className="font-mono-data text-2xl mt-1 text-[var(--text)]">{numDisplay}</div>
      {sublabel && (
        <div className="text-[11px] text-[var(--muted-text)] mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}

export function AgingBar({ buckets, currency = "USD", testidPrefix = "aging" }) {
  const order = ["current", "1-30", "31-60", "61-90", "90+"];
  const colors = {
    "current": "bg-[var(--brand)]",
    "1-30": "bg-emerald-400",
    "31-60": "bg-amber-400",
    "61-90": "bg-orange-500",
    "90+": "bg-red-600",
  };
  const total = order.reduce((s, k) => s + (buckets[k] || 0), 0);
  if (total <= 0.005) {
    return (
      <div className="text-sm text-[var(--muted-text)] py-4 text-center">
        Nothing outstanding — all caught up.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex rounded-md overflow-hidden h-3 border border-[var(--border)]" data-testid={`${testidPrefix}-bar`}>
        {order.map((k) => {
          const v = buckets[k] || 0;
          const pct = (v / total) * 100;
          if (pct <= 0) return null;
          return (
            <div
              key={k}
              className={colors[k]}
              style={{ width: `${pct}%` }}
              title={`${k}: ${formatCurrency(v, currency)}`}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-5 gap-2 text-xs">
        {order.map((k) => (
          <div key={k} data-testid={`${testidPrefix}-cell-${k}`}>
            <div className="flex items-center gap-1.5">
              <span className={`inline-block w-2 h-2 rounded-sm ${colors[k]}`} />
              <span className="text-[var(--muted-text)] uppercase tracking-wider text-[10px]">{k}</span>
            </div>
            <div className="font-mono-data mt-0.5">{formatCurrency(buckets[k] || 0, currency)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
